import { Analytics } from '@cobliteam/analytics';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import useChangeFleet from '@cobliteam/hooks/src/useSession/useChangeFleet';
import { useTranslation } from '@cobliteam/i18n';
import Divider from '@cobliteam/ui/components/Divider';
import DropdownMenu from '@cobliteam/ui/components/DropdownMenu';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Input from '@cobliteam/ui/form/Input';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@cobliteam/ui-business/components/AppSidebar/components/Sidebar';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from './AppFleetAvatar';
import useFleetAccessRanking from './useFleetAccessRanking';

interface Fleet {
  name: string;
  id: string;
}

interface FleetSwitcherProps {
  fleets: Fleet[];
  currentFleet: Fleet;
}

const getFleetInitials = (fleetName: string) => {
  return fleetName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
};

/**
 * Renderiza o seletor de frota no sidebar, com pesquisa, lista de frotas ranqueada por acessos e opção de troca.
 *
 * Ao abrir o menu registra um evento de analytics e dá foco no campo de pesquisa; ao selecionar outra frota despacha a ação de mudança de frota,
 * incrementa o ranking de acesso local (localStorage) e registra o evento correspondente.
 *
 * @param fleets - Lista de frotas disponíveis exibidas e filtradas; cada item deve conter pelo menos `id` e `name`.
 * @param currentFleet - Frota atualmente selecionada; deve conter `id` e `name`.
 * @returns O elemento React que representa o componente AppFleetSwitcher.
 */
function AppFleetSwitcher({ fleets = [], currentFleet = { name: '', id: '' } }: FleetSwitcherProps) {
  const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { mutate: changeFleet } = useChangeFleet();
  const { runtimeFlags } = useDashboard();
  const { openMobile } = useSidebar();
  const { t } = useTranslation('ui-business');
  const { sortFleetsByAccess, trackFleetAccess } = useFleetAccessRanking();

  const handleChangeFleet = (fleetId: string, fleetName: string) => {
    trackFleetAccess(fleetId);
    changeFleet({ id: fleetId, name: fleetName });
    Analytics.track('FleetSwitcher - Switch - Fleet', { fleetId, fleetName });
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      Analytics.track('FleetSwitcher - Open');
    } else {
      setSearch('');
    }
    setOpen(isOpen);
  };

  /**
   * Radix foca o container do menu ao abrir; aqui o ponto de entrada é a pesquisa.
   *
   * `onOpenAutoFocus` não está nos tipos públicos do DropdownMenu, mas o MenuContent o compõe em runtime
   * (`composeEventHandlers(onOpenAutoFocus, ...)`), então o `preventDefault` abaixo cancela o foco padrão.
   */
  const searchAutoFocusProps: { onOpenAutoFocus?: (event: Event) => void } = {
    onOpenAutoFocus: (event) => {
      if (!searchInputRef.current) return;
      event.preventDefault();
      searchInputRef.current.focus();
    },
  };

  const filteredFleets = sortFleetsByAccess(fleets).filter((f) => f.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={open} onOpenChange={handleOpenChange}>
          <DropdownMenu.Trigger asChild>
            {openMobile ? (
              <SidebarMenuButton
                data-testid='fleet-switcher'
                size='lg'
                className='h-11 cursor-pointer px-2 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
              >
                <div className='flex aspect-square items-center justify-center rounded-lg text-sidebar-primary-foreground'>
                  <Avatar className='size-8 rounded-md'>
                    {!isDemoMode && <AvatarImage src={`https://assets.cobli.co/fleets/${currentFleet.id}/logo.png`} />}
                    <AvatarFallback className='rounded-md bg-primary-9 font-semibold text-white text-xs'>{getFleetInitials(currentFleet.name)}</AvatarFallback>
                  </Avatar>
                </div>
                <div className='grid min-w-0 flex-1 text-left text-sm leading-5'>
                  <span className='truncate font-semibold'>{currentFleet.name}</span>
                </div>
                <Icon name='chevrons-up-down' size='sm' className='ml-auto text-gray-6' />
              </SidebarMenuButton>
            ) : (
              <div className='mt-1' data-testid='fleet-switcher'>
                <div className='flex items-center justify-center text-sidebar-primary-foreground'>
                  <Tooltip content={currentFleet.name} align='center' position='right' delayDuration={400}>
                    <Avatar className='!cursor-pointer size-9 rounded-lg shadow-sm'>
                      {!isDemoMode && <AvatarImage src={`https://assets.cobli.co/fleets/${currentFleet.id}/logo.png`} className='rounded-lg border border-gray-6' />}
                      <AvatarFallback className='rounded-lg bg-primary-9 font-semibold text-white text-xs'>{getFleetInitials(currentFleet.name)}</AvatarFallback>
                    </Avatar>
                  </Tooltip>
                </div>
              </div>
            )}
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            portal={!openMobile}
            className={cn('-mt-3 w-80 rounded-lg border-gray-6 px-0 pt-2 pb-2 text-sidebar-foreground')}
            alignOffset={10}
            align='start'
            side={openMobile ? 'bottom' : 'right'}
            collisionPadding={{ left: 10 }}
            {...searchAutoFocusProps}
          >
            <section className='flex flex-col pt-2 pr-2 pb-2 pl-4 font-medium text-gray-12 text-sm'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <span>Lista de Frotas</span>
                </div>
                {!runtimeFlags.noValidGroupOrVehicles && (
                  <Tooltip content='Configurações' portal={false} asChild>
                    <Link to='/config/account' className='cursor-pointer' onClick={() => handleOpenChange(false)}>
                      <Icon name='settings' className='mr-1 size-5 text-gray-10' />
                    </Link>
                  </Tooltip>
                )}
              </div>
            </section>
            <Divider className='mb-2' />
            {fleets.length > 1 && (
              <div className='px-3 pb-2'>
                <Input
                  ref={searchInputRef}
                  placeholder={t('fleet_switcher.search_placeholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  startIcon={<Icon name='search' className='size-4 text-gray-9' />}
                  endIcon={
                    search ? (
                      <button type='button' aria-label={t('fleet_switcher.clear_search')} onClick={() => setSearch('')} className='flex cursor-pointer items-center justify-center'>
                        <Icon name='x' className='size-4 text-gray-9 hover:text-gray-12' />
                      </button>
                    ) : undefined
                  }
                />
              </div>
            )}
            <SidebarMenu className='px-0'>
              <div
                className={cn(
                  'flex max-h-96 flex-col gap-2 overflow-y-auto',
                  'scrollbar',
                  'scrollbar-thumb-custom',
                  'scrollbar-thin',
                  'scrollbar-thumb-rounded-full scrollbar-track-rounded-full',
                  'scrollbar-thumb-gray-8',
                  'scrollbar-thumb-bg-clip-content',
                  'scrollbar-track-gray-1'
                )}
              >
                {filteredFleets.map((fleet) => {
                  const isActive = currentFleet.id === fleet.id;
                  const fleetLogo = `https://assets.cobli.co/fleets/${fleet.id}/logo.png`;
                  const fleetAvatarFallback = `https://ui-avatars.com/api/?name=${fleet.name}&background=0234E1&color=fff`;

                  return (
                    <SidebarMenuItem key={fleet.id} className='px-2'>
                      <SidebarMenuButton onClick={() => handleChangeFleet(fleet.id, fleet.name)} className='cursor-pointer px-0.5 text-xs capitalize' isActive={isActive}>
                        <Avatar className='size-8 rounded-lg border border-gray-5'>
                          <AvatarImage src={fleetLogo || fleetAvatarFallback} className='rounded-md' />
                          <AvatarFallback className='font-medium text-white'>
                            <img src={fleetAvatarFallback} alt={fleet.name} className='size-full' />
                          </AvatarFallback>
                        </Avatar>
                        <span className='min-w-0 flex-1 truncate font-medium text-sm'>{fleet.name.toLocaleLowerCase()}</span>
                        {isActive && <Icon name='check' className='ml-auto' />}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </div>
            </SidebarMenu>
          </DropdownMenu.Content>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export default AppFleetSwitcher;
